import { useState, useEffect, useCallback } from 'react'
import { set, get } from 'idb-keyval'
import api from '@/services/api'
import { getTenantId } from '@/services/tokenStorage'
import { getEchoInstanceId, registerEchoListener, onRealtimeResync } from '@/services/echoClient'
// NOTE: eventBus kept here — this is legitimate cross-component communication
// for LAN sync peer notifications that cannot be replaced by RTK Query tags.
// Using fireEvent directly (not useEventBus hook) to avoid hook-rules violation.
import { fireEvent } from '@/utils/eventBus'
import i18n from '@/i18n'

const TRANSACTIONS_KEY = 'lan-sync-transactions'
const MAX_LAN_TRANSACTIONS = 200

function newTxId(): string {
  try {
    const uuid = (globalThis as any)?.crypto?.randomUUID?.()
    if (uuid) return `tx-${uuid}`
  } catch {
    // fall through to Date+random
  }
  return `tx-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * ICE servers for the LAN peer mesh.
 *
 * Peers in a LAN store are on the same subnet, where host candidates negotiate
 * fine — and a public STUN server requires internet access that an on-prem
 * installation may not have, so hardcoding Google STUN made an air-gapped
 * store's peer mesh fail to connect. Override when peers must traverse NAT:
 * VITE_LAN_ICE_SERVERS (JSON array) or VITE_LAN_STUN_URL (single URL).
 */
function resolveIceServers(): RTCIceServer[] {
  const configured = import.meta.env.VITE_LAN_ICE_SERVERS
  if (configured) {
    try {
      const parsed = JSON.parse(configured)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed as RTCIceServer[]
      }
    } catch {
      if (import.meta.env.DEV) console.warn('[lan-sync] VITE_LAN_ICE_SERVERS is not valid JSON — using host candidates only.')
    }
  }

  const stunUrl = import.meta.env.VITE_LAN_STUN_URL
  return stunUrl ? [{ urls: stunUrl }] : []
}

const ICE_SERVERS: RTCIceServer[] = resolveIceServers()

export interface LanPeer {
  id: string
  name: string
  connectedAt: string
  dc: RTCDataChannel | null
  pc: RTCPeerConnection | null
}

export interface LanTransaction {
  id: string
  type: string
  peerId: string
  data: Record<string, unknown>
  timestamp: string
  synced: boolean
}

const peerId = `peer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
const peerName = (() => {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('pos_peer_name') : null
  if (stored) return stored
  const name = `POS-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
  if (typeof localStorage !== 'undefined') localStorage.setItem('pos_peer_name', name)
  return name
})()

let peers = new Map<string, LanPeer>()
let transactions: LanTransaction[] = []
let transactionsTenantId: string | null = null
let transactionsGeneration = 0
// The signalling channel is registered through the echoClient registry, so a
// rebuilt Echo instance (logout → fresh login) replays it automatically —
// the stale-channel-after-logout bug is fixed in the registry, not here.
// We only keep the unregister fn plus the instance id we registered against
// (peers authorized with an old bearer token must be dropped on rebuild).
let unregisterLanSignal: (() => void) | null = null
let echoInstanceIdAtRegistration: number | null = null
const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach(l => l())
}

/**
 * DataChannel.send() throws synchronously when the channel is not open — a peer
 * can drop between the `readyState` check and the send, or `onopen` can fire
 * before the state fully settles. An unguarded throw aborted the rest of
 * `onopen` / `broadcastToPeers`, so one flaky peer broke sync for every other
 * peer. Best-effort: swallow (log in dev) instead.
 */
function safeSend(dc: RTCDataChannel | null | undefined, payload: string): void {
  if (!dc || dc.readyState !== 'open') return
  try {
    dc.send(payload)
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[lan-sync] data channel send failed:', err)
    }
  }
}

function saveTransactions(): void {
  if (!transactionsTenantId || transactionsTenantId !== getTenantId()) return
  // Bounded log: drop oldest synced rows first, then oldest overall, so a
  // long-lived terminal cannot grow IndexedDB forever and a new peer is not
  // flooded with the full history on `full-state`.
  if (transactions.length > MAX_LAN_TRANSACTIONS) {
    // Append-ordered log (newest last): keep the newest rows overall, dropping
    // the OLDEST already-synced rows first so pending rows are never evicted.
    // Reordering unsynced ahead of synced (the old code) both scrambled the log
    // and discarded unsynced rows from the front of the queue.
    const overflow = transactions.length - MAX_LAN_TRANSACTIONS
    const droppable = transactions.filter((t) => t.synced).slice(0, overflow)
    const dropIds = new Set(droppable.map((t) => t.id))
    transactions = transactions.filter((t) => !dropIds.has(t.id)).slice(-MAX_LAN_TRANSACTIONS)
  }
  set(`${TRANSACTIONS_KEY}:${transactionsTenantId}`, transactions).catch(() => {})
}

function restoreTransactions(): void {
  const tenantId = getTenantId()
  if (transactionsTenantId === tenantId) return
  transactionsTenantId = tenantId
  const generation = ++transactionsGeneration
  transactions = []
  closeAllPeers()
  if (!tenantId) return
  // The old unscoped key has no trustworthy tenant provenance. Keep it on
  // disk for manual recovery, but never send those rows to another tenant.
  get<LanTransaction[]>(`${TRANSACTIONS_KEY}:${tenantId}`).then((saved) => {
    if (generation !== transactionsGeneration || getTenantId() !== tenantId) return
    if (saved?.length) {
      // A sale may have been added while IndexedDB was loading.
      const liveIds = new Set(transactions.map((tx) => tx.id))
      transactions = [...saved.filter((tx) => !liveIds.has(tx.id)), ...transactions].slice(-MAX_LAN_TRANSACTIONS)
      emitChange()
    }
  }).catch(() => {})
}

function notifyPeerConnected(name: string): void {
  fireEvent('notification:add', {
    type: 'success',
    title: i18n.t('Lan_peer_connected_title'),
    message: i18n.t('Lan_peer_connected_message', { name }),
    duration: 4000,
  })
}

function notifyPeerDisconnected(name: string): void {
  fireEvent('notification:add', {
    type: 'warning',
    title: i18n.t('Lan_peer_disconnected_title'),
    message: i18n.t('Lan_peer_disconnected_message', { name }),
    duration: 3000,
  })
}

async function signalServer(signalType: string, payload: unknown): Promise<void> {
  try {
    const toPeerId = (payload as any)?.toPeerId ?? (payload as any)?.to_peer_id
    // fromPeerId travels top-level so the server can echo our terminal's
    // identity back in the broadcast — every terminal self-filters on it.
    await api.post('/lan-signal', { signalType, fromPeerId: peerId, toPeerId, payload })
  } catch (err) {
    // Signalling is best-effort; keep production silent.
    if (import.meta.env.DEV) {
      console.warn('[lan-sync] signalServer failed:', signalType, err)
    }
  }
}

function handleRemotePeerSignal(data: {
  signalType?: string
  signal_type?: string
  fromPeerId?: string
  from_peer_id?: string
  toPeerId?: string
  to_peer_id?: string
  fromPeerName?: string
  from_peer_name?: string
  payload: unknown
}): void {
  const signalType = data.signalType ?? data.signal_type
  const fromPeerId = data.fromPeerId ?? data.from_peer_id
  const fromPeerName = data.fromPeerName ?? data.from_peer_name ?? ''
  if (!signalType || !fromPeerId || fromPeerId === peerId) return

  const rawPayload = data.payload as any
  const targetPeerId = data.toPeerId ?? data.to_peer_id ?? rawPayload?.toPeerId ?? rawPayload?.to_peer_id
  if (targetPeerId && targetPeerId !== peerId) {
    // Signal was directed to a specific other peer in the mesh — ignore
    return
  }

  switch (signalType) {
    case 'peer-announce':
      runSignalHandler(handlePeerAnnounce(fromPeerId, fromPeerName))
      break
    case 'offer': {
      const sdp = rawPayload?.sdp ?? rawPayload
      if (sdp?.type && sdp?.sdp) {
        runSignalHandler(handleOffer(fromPeerId, fromPeerName, sdp as RTCSessionDescriptionInit))
      }
      break
    }
    case 'answer': {
      const sdp = rawPayload?.sdp ?? rawPayload
      if (sdp?.type && sdp?.sdp) {
        runSignalHandler(handleAnswer(fromPeerId, sdp as RTCSessionDescriptionInit))
      }
      break
    }
    case 'ice-candidate': {
      const candidate = rawPayload?.candidate ?? rawPayload
      if (candidate) {
        runSignalHandler(handleIceCandidate(fromPeerId, candidate as RTCIceCandidateInit))
      }
      break
    }
    case 'peer-leave':
      removePeer(fromPeerId)
      break
  }
}

// createOffer/createAnswer/setRemoteDescription can reject on invalid SDP or an
// already-closed peer connection — dispatching without catching turned those
// into unhandled promise rejections.
function runSignalHandler(task: Promise<void>): void {
  task.catch((err) => {
    if (import.meta.env.DEV) {
      console.warn('[lan-sync] signal handling failed:', err)
    }
  })
}

function createPC(remotePeerId: string, isInitiator: boolean): RTCPeerConnection {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      signalServer('ice-candidate', {
        toPeerId: remotePeerId,
        candidate: event.candidate.toJSON(),
      })
    }
  }

  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed') {
      const peer = peers.get(remotePeerId)
      if (peer) {
        notifyPeerDisconnected(peer.name)
        removePeer(remotePeerId)
      }
      pc.close()
    }
  }

  if (isInitiator) {
    const dc = pc.createDataChannel('lan-sync', { ordered: true })
    setupDataChannel(dc, remotePeerId)
  } else {
    pc.ondatachannel = (event) => {
      setupDataChannel(event.channel, remotePeerId)
    }
  }

  return pc
}

function setupDataChannel(dc: RTCDataChannel, remotePeerId: string): void {
  dc.onopen = () => {
    const existing = peers.get(remotePeerId)
    const name = existing?.name || remotePeerId
    const pc = existing?.pc || null

    peers.set(remotePeerId, {
      id: remotePeerId,
      name,
      connectedAt: new Date().toISOString(),
      dc,
      pc,
    })
    peers = new Map(peers)
    emitChange()

    safeSend(dc, JSON.stringify({ type: 'peer-info', peerId, peerName }))
    sendFullState(dc)
    notifyPeerConnected(name)
  }

  dc.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data)
      handleDataMessage(msg, remotePeerId)
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[lan-sync] failed to parse peer data message:', err)
      }
    }
  }

  dc.onclose = () => {
    const peer = peers.get(remotePeerId)
    if (peer) {
      notifyPeerDisconnected(peer.name)
      removePeer(remotePeerId)
    }
  }
}

function handleDataMessage(msg: Record<string, unknown>, remotePeerId: string): void {
  switch (msg.type) {
    case 'peer-info': {
      const peer = peers.get(remotePeerId)
      if (peer) {
        peer.name = msg.peerName as string
        peers = new Map(peers)
        emitChange()
      }
      break
    }
    case 'transaction': {
      const tx = msg.transaction as LanTransaction
      if (!transactions.find((t) => t.id === tx.id)) {
        transactions = [...transactions, tx]
        saveTransactions()
        emitChange()
        fireEvent('lan:transaction-received', tx)
      }
      break
    }
    case 'full-state': {
      const remoteTxs = msg.transactions as LanTransaction[]
      let added = false
      const newTransactions = [...transactions]
      for (const tx of remoteTxs) {
        if (!newTransactions.find((t) => t.id === tx.id)) {
          newTransactions.push(tx)
          added = true
          fireEvent('lan:transaction-received', tx)
        }
      }
      if (added) {
        transactions = newTransactions
        saveTransactions()
        emitChange()
      }
      break
    }
  }
}

function sendFullState(dc: RTCDataChannel): void {
  safeSend(dc, JSON.stringify({ type: 'full-state', transactions }))
}

async function handlePeerAnnounce(remotePeerId: string, remotePeerName: string): Promise<void> {
  if (peers.has(remotePeerId)) return

  const pc = createPC(remotePeerId, true)
  peers.set(remotePeerId, {
    id: remotePeerId,
    name: remotePeerName,
    connectedAt: new Date().toISOString(),
    dc: null,
    pc,
  })
  peers = new Map(peers)
  emitChange()

  try {
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)

    // setLocalDescription resolves once localDescription is populated, so the
    // former busy-wait (setTimeout spinning until localDescription.sdp) was
    // redundant and could spin forever on a failed connection.
    signalServer('offer', { toPeerId: remotePeerId, sdp: pc.localDescription })
  } catch (err) {
    // The peer entry was inserted before negotiation even started; leaving it
    // would make peers.has() reject every future re-announce and wedge the
    // peer out for the rest of the session. Drop it so the next announce can
    // retry — but only if a newer attempt hasn't replaced it.
    dropFailedPeer(remotePeerId, pc)
    if (import.meta.env.DEV) {
      console.warn('[lan-sync] offer negotiation failed after peer-announce:', remotePeerId, err)
    }
  }
}

async function handleOffer(remotePeerId: string, remotePeerName: string, sdp: RTCSessionDescriptionInit): Promise<void> {
  if (peers.has(remotePeerId)) return

  const pc = createPC(remotePeerId, false)
  peers.set(remotePeerId, {
    id: remotePeerId,
    name: remotePeerName,
    connectedAt: new Date().toISOString(),
    dc: null,
    pc,
  })
  peers = new Map(peers)
  emitChange()

  try {
    await pc.setRemoteDescription(new RTCSessionDescription(sdp))

    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)

    // See handlePeerAnnounce: setLocalDescription already guarantees a local
    // description, so the busy-wait loop was redundant.
    signalServer('answer', { toPeerId: remotePeerId, sdp: pc.localDescription })
  } catch (err) {
    // Same wedge as handlePeerAnnounce: a half-negotiated entry blocks every
    // later offer/announce from this peer until the session ends.
    dropFailedPeer(remotePeerId, pc)
    if (import.meta.env.DEV) {
      console.warn('[lan-sync] answer negotiation failed:', remotePeerId, err)
    }
  }
}

async function handleAnswer(remotePeerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
  const peer = peers.get(remotePeerId)
  if (!peer?.pc) return

  await peer.pc.setRemoteDescription(new RTCSessionDescription(sdp))
}

async function handleIceCandidate(remotePeerId: string, candidate: RTCIceCandidateInit): Promise<void> {
  const peer = peers.get(remotePeerId)
  if (!peer?.pc) return

  try {
    await peer.pc.addIceCandidate(new RTCIceCandidate(candidate))
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[lan-sync] addIceCandidate failed:', remotePeerId, err)
    }
  }
}

function removePeer(id: string): void {
  const peer = peers.get(id)
  if (peer) {
    peer.dc?.close()
    peer.pc?.close()
    peers.delete(id)
    peers = new Map(peers)
    emitChange()
  }
}

// Negotiation-failure cleanup: drop the entry ONLY if it still belongs to the
// failed attempt — a concurrent re-announce must never be erased by the old
// attempt's rejection.
function dropFailedPeer(id: string, pc: RTCPeerConnection): void {
  if (peers.get(id)?.pc === pc) {
    removePeer(id)
  }
}

function broadcastToPeers(type: string, data: Record<string, unknown>): void {
  restoreTransactions()
  if (!transactionsTenantId) return
  const tx: LanTransaction = {
    id: newTxId(),
    type,
    peerId,
    data,
    timestamp: new Date().toISOString(),
    synced: false,
  }

  transactions = [...transactions, tx]
  saveTransactions()
  emitChange()

  const msg = JSON.stringify({ type: 'transaction', transaction: tx })
  for (const peer of peers.values()) {
    safeSend(peer.dc, msg)
  }
}

// M-16 — the signalling channel is private and tenant-scoped
// (private-pos.lan-sync.{tenantId}); Echo authorizes it via
// POST /broadcasting/auth with the Bearer + X-Tenant-ID headers.
function lanSyncChannelName(): string | null {
  const tenantId = getTenantId()
  return tenantId ? `pos.lan-sync.${tenantId}` : null
}

function initEchoChannel(): void {
  try {
    restoreTransactions()
    const instanceId = getEchoInstanceId()
    if (instanceId === null) return

    // Already registered against the live instance — nothing to do.
    if (unregisterLanSignal && echoInstanceIdAtRegistration === instanceId) return

    if (unregisterLanSignal) {
      // The channel belongs to a dead Echo instance (logout / tenant switch):
      // drop the registration together with the peers that were signalled
      // over it — those connections cannot be re-authorized without the old
      // bearer token.
      unregisterLanSignal()
      unregisterLanSignal = null
      closeAllPeers()
    }

    const channelName = lanSyncChannelName()
    if (!channelName) return

    unregisterLanSignal = registerEchoListener(channelName, '.LanPeerSignal', handleRemotePeerSignal)
    echoInstanceIdAtRegistration = instanceId

    signalServer('peer-announce', { peerId, peerName })
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[lan-sync] initEchoChannel failed:', err)
    }
  }
}

function closeAllPeers(): void {
  for (const peer of peers.values()) {
    peer.dc?.close()
    peer.pc?.close()
  }
  peers.clear()
  peers = new Map()
  emitChange()
}

function destroyEchoChannel(): void {
  if (unregisterLanSignal) {
    unregisterLanSignal()
    unregisterLanSignal = null
  }
  echoInstanceIdAtRegistration = null
}

if (typeof window !== 'undefined') {
  restoreTransactions()
  initEchoChannel()
}

export function useLanSync() {
  const [, setTick] = useState(0)

  useEffect(() => {
    const handler = () => setTick(t => t + 1)
    listeners.add(handler)
    // The module-level init runs at import time, before login/Echo may be
    // ready — retry on first hook mount when the channel is still missing.
    // initEchoChannel itself detects an Echo rebuild (stale instance id) and
    // drops the peers that cannot be re-authorized before re-registering.
    initEchoChannel()

    const unbindResync = onRealtimeResync(() => {
      initEchoChannel()
    })

    // Re-check on connectivity/visibility transitions + Echo rebuilds
    // (via resync) instead of perpetual polling: the old 3s interval woke
    // every mounted consumer forever even when the channel was healthy.
    const onOnline = () => initEchoChannel()
    const onVisible = () => {
      if (document.visibilityState === 'visible') initEchoChannel()
    }
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      listeners.delete(handler)
      unbindResync()
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const isConnected = peers.size > 0
  const peerCount = peers.size
  const peerList = Array.from(peers.values()).map((p) => ({ id: p.id, name: p.name }))

  function broadcastTransaction(type: string, data: Record<string, unknown>): void {
    broadcastToPeers(type, data)
  }

  // The former unsyncedCount / getUnsyncedTransactions / markTransactionSynced
  // trio was removed. Nothing ever called markTransactionSynced, so the
  // `synced` flag stayed false forever and the "unsynced" badge in
  // PeerConnectModal only ever grew — it is now gone. The authoritative
  // pending-sync count lives in the POS offline indicator, which is backed by
  // the pending_sales queue the peer sale is actually imported into.

  const disconnectAll = useCallback(() => {
    signalServer('peer-leave', {})
    closeAllPeers()
    destroyEchoChannel()
  }, [])

  // NOTE: unmounting a consumer must NOT disconnect — the peers map and the
  // `pos.lan-sync` channel are module-level singletons shared by the whole
  // app (background peer sync keeps running with no component mounted).
  // Tearing them down here meant closing PeerConnectModal killed LAN sync for
  // the rest of the session. Logout/tenant switch is handled by the Echo
  // owner check above; an explicit disconnect uses `disconnectAll`.

  return {
    peerId,
    peerName,
    peers: peerList,
    isConnected,
    peerCount,
    transactions,
    broadcastTransaction,
    disconnectAll,
  }
}
